/**
 * MobileShell — chrome for the /staff-app PWA.
 *
 * Reusable layout wrapper:
 *   • Sticky top-bar with role badge + logout
 *   • Safe-area padding for iOS notch and Android nav bar
 *   • Optional bottom-nav (crew leader / supervisor use it)
 *   • Single-column, minimum 48px touch targets everywhere
 *
 * Iter-142 · Field Staff Mobile PWA Addendum.
 */
import { useAuth } from '../../contexts/AuthContext';
import { LogOut } from 'lucide-react';

const GOLD = '#C9A962';

export function MobileShell({
  title,
  subtitle,
  bottomNav = null,
  children,
  testId = 'staff-app-shell',
}) {
  const { user, logout } = useAuth();
  const roleLabel = (user?.role || '').replace(/_/g, ' ').toUpperCase();

  return (
    <div
      className="min-h-screen bg-zinc-950 text-white flex flex-col"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: bottomNav ? 0 : 'env(safe-area-inset-bottom)',
      }}
      data-testid={testId}
    >
      {/* Top bar */}
      <header
        className="sticky top-0 z-40 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur"
        data-testid={`${testId}-topbar`}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <div
            className="h-9 w-9 rounded-full grid place-items-center font-black text-black text-xs"
            style={{ background: GOLD }}
          >
            NECS
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate" data-testid={`${testId}-title`}>{title}</p>
            {subtitle && (
              <p className={user?.name && subtitle === user.name ? 'necs-dashboard-name text-base text-zinc-200' : 'text-[10px] text-zinc-400 truncate'} data-testid={`${testId}-subtitle`}>{subtitle}</p>
            )}
          </div>
          <span
            className="text-[9px] uppercase tracking-widest font-black rounded-full px-2 py-1"
            style={{ background: 'rgba(201,169,98,0.15)', color: GOLD }}
            data-testid={`${testId}-role-badge`}
          >
            {roleLabel || 'STAFF'}
          </span>
          <button
            type="button"
            onClick={logout}
            className="min-h-[48px] min-w-[48px] grid place-items-center rounded-full text-zinc-400 hover:text-white"
            aria-label="Log out"
            data-testid={`${testId}-logout`}
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Content */}
      <main
        className="flex-1 px-4 py-4 space-y-4"
        style={{ paddingBottom: bottomNav ? '84px' : '16px' }}
        data-testid={`${testId}-main`}
      >
        {children}
      </main>

      {/* Bottom nav */}
      {bottomNav && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-zinc-800 bg-zinc-950/95 backdrop-blur"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          data-testid={`${testId}-bottomnav`}
        >
          <div className="grid grid-cols-5 gap-1 px-1 py-1">
            {bottomNav}
          </div>
        </nav>
      )}
    </div>
  );
}

export function BottomNavButton({ icon: Icon, label, active, onClick, badge = 0, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-[56px] rounded-lg flex flex-col items-center justify-center gap-0.5 transition-colors ${
        active ? 'bg-[#C9A962]/15 text-[#FFD60A]' : 'text-zinc-400 hover:text-white'
      }`}
      data-testid={testId}
      aria-label={label}
    >
      <span className="relative">
        <Icon className="h-5 w-5" />
        {badge > 0 && (
          <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-black grid place-items-center">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </span>
      <span className="text-[9px] font-bold uppercase tracking-wide">{label}</span>
    </button>
  );
}

export default MobileShell;
